import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Panel } from "@/components/app/PageParts";
import { DraftField } from "@/components/app/DraftField";
import { AnswerSheetConfirm } from "@/components/app/AnswerSheetConfirm";
import { AnswerSheetBadge, KindBadge, ManualGuidanceNote } from "@/components/app/FormBits";
import { PropertyTag } from "@/components/app/PropertySelect";
import { api, errorMessage } from "@/lib/api";
import { blobError } from "@/lib/format";

const TYPE_LABEL = { missing: "Missing", format: "Formatting", inconsistency: "Check this", unresolved: "Not sure yet" };

const CheckPanel = ({ check, onAck }) => (
  <Panel title={check.label} testId="completeness-check-panel">
    <div className="space-y-3 p-5">
      {check.issues.length === 0 ? <p data-testid="completeness-no-issues" className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> No missing answers or formatting problems found.</p> : (
        <ul className="space-y-2" data-testid="completeness-issues">{check.issues.map((i, n) => (
          <li key={n} data-testid={`completeness-issue-${i.type}`} className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <span className="mr-2 rounded bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase">{TYPE_LABEL[i.type]}</span><strong>{i.label}</strong> {i.message}
            {i.can_acknowledge && <button onClick={() => onAck(i.rule_id)} data-testid={`completeness-ack-${i.rule_id}`} className="ml-2 text-xs font-medium underline">I've checked these answers</button>}
          </li>))}</ul>
      )}
      <p className="text-xs text-slate-500" data-testid="completeness-disclaimer">{check.disclaimer}</p>
    </div>
  </Panel>
);

const Review = ({ draft, onConfirm, onExport, exporting, result }) => {
  const { check, version } = draft;
  const [confirmOpen, setConfirmOpen] = useState(false);
  useEffect(() => { if (result) setConfirmOpen(false); }, [result]);
  const answered = version.fields.filter((f) => f.kind !== "self" && draft.answers[f.key] !== undefined);
  const ready = version.usable && !check.issues.length && !check.unconfirmed.length && check.answered > 0;
  const shown = (f) => f.kind === "choice" ? Object.fromEntries(f.options)[draft.answers[f.key]] : f.kind === "checkbox" ? (draft.answers[f.key] ? "Checked" : "Not checked") : draft.answers[f.key];
  return (
    <div className="space-y-6">
      <Panel title="Final review: confirm every answer" testId="final-review-panel">
        <ul className="divide-y">{answered.map((f) => (
          <li key={f.key} className="flex items-start gap-3 px-5 py-3">
            <Checkbox checked={!check.unconfirmed.includes(f.key)} disabled={!check.unconfirmed.includes(f.key)} onCheckedChange={() => onConfirm(f.key)} data-testid={`review-confirm-${f.key}`} className="mt-0.5" />
            <div><p className="text-xs text-slate-500">{f.number}: {f.label}</p><p className="text-sm font-medium text-navy">{shown(f)}</p></div>
          </li>))}
          {version.fields.filter((f) => f.kind === "self").map((f) => <li key={f.key} className="px-5 py-3 text-xs text-slate-500">{f.number}: {f.label}: you complete this yourself.</li>)}
        </ul>
      </Panel>
      <div className="rounded-xl border bg-white p-5">
        {!version.usable && <p data-testid="export-paused" className="mb-3 text-sm text-amber-900">Export is paused for this form version. Your answers are saved.</p>}
        {!ready && version.usable && <p data-testid="export-not-ready" className="mb-3 text-sm text-slate-600">Resolve the completeness check and confirm each answer to export.</p>}
        {!version.fillable && <p data-testid="export-answer-sheet-notice" className="mb-3 text-sm text-amber-900">This form gives you an answer sheet only. The official form will not be filled.</p>}
        <Button onClick={() => version.fillable ? onExport(false) : setConfirmOpen(true)} disabled={!ready || exporting} data-testid="draft-export-button" className="bg-navy hover:bg-navy-2"><Download className="mr-1.5 h-4 w-4" />
          {version.fillable ? "Download filled draft PDF" : "Download answer sheet"}</Button>
        {!version.fillable && <AnswerSheetConfirm open={confirmOpen} onOpenChange={setConfirmOpen} form={draft.form} version={version} busy={exporting} onDownload={() => onExport(true)} />}
        {result && <p data-testid={`export-result-${result}`} className="mt-3 rounded-md border border-gold/40 bg-gold-light/50 p-3 text-sm text-navy">
          {result === "filled_pdf" ? "Filled draft PDF downloaded. Open it and check every value, then sign, date and submit it yourself." : "Answer sheet only — the official form has not been filled. Copy your answers onto the official form, then sign and submit it yourself."}
        </p>}
      </div>
    </div>
  );
};

export default function DraftEditor() {
  const { workspace, properties } = useOutletContext();
  const { draftId } = useParams();
  const [draft, setDraft] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);
  const url = `/workspaces/${workspace.id}/drafts/${draftId}`;

  const load = useCallback(() => { api.get(url).then(({ data }) => setDraft(data.draft)).catch((e) => toast.error(errorMessage(e))); }, [url]);
  useEffect(load, [load]);
  const patch = async (body) => { try { const { data } = await api.patch(url, body); setDraft(data.draft); } catch (e) { toast.error(errorMessage(e)); } };
  const confirm = async (key) => { try { const { data } = await api.post(`${url}/confirm`, { keys: [key] }); setDraft(data.draft); } catch (e) { toast.error(errorMessage(e)); } };
  const exportDraft = async (ack) => {
    setExporting(true); setResult(null);
    try {
      const res = await api.post(`${url}/export`, ack ? { answer_sheet_acknowledged: true } : null, { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "export";
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = href; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 2000);
      setResult(res.headers["x-evr-export-kind"]);
      load();
    } catch (e) { toast.error((await blobError(e)) || errorMessage(e)); } finally { setExporting(false); }
  };

  if (!draft) return null;
  const v = draft.version;
  return (
    <div data-testid="draft-editor-page">
      <PageHeader eyebrow={`${draft.form.number} · ${v.revision}`} title={draft.title} subtitle={draft.form.title}
        action={<Link to={`/app/w/${workspace.id}/forms`} className="text-sm text-slate-500 hover:text-navy" data-testid="draft-back-link">Back to forms</Link>} />
      <div className="mb-6 flex flex-wrap items-center gap-3"><KindBadge kind={draft.form.kind} /><PropertyTag properties={properties} id={draft.property_id} />
        {v.source_url && <a href={v.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-navy underline" data-testid="draft-official-link">Official form <ExternalLink className="h-3 w-3" /></a>}
        {!v.fillable && <AnswerSheetBadge testId="draft-answer-sheet-badge" />}</div>
      {!v.usable && <p data-testid="draft-version-paused" className="mb-6 flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"><AlertTriangle className="h-5 w-5 shrink-0" />This form version is {v.status.replace(/_/g, " ")}{v.note ? `: ${v.note}` : ""}. New use and export are paused. Your answers are kept and won't be moved to another version.</p>}
      <div className="mb-6"><ManualGuidanceNote /></div>
      <Tabs defaultValue="answer">
        <TabsList className="mb-6"><TabsTrigger value="answer" data-testid="draft-tab-answer">Answer questions</TabsTrigger><TabsTrigger value="review" data-testid="draft-tab-review">Final review</TabsTrigger></TabsList>
        <TabsContent value="answer" className="space-y-4">
          {v.fields.map((f) => <DraftField key={f.key} f={f} draft={draft} isSample={workspace.is_sample} onSave={(k, val) => patch({ answers: { [k]: val } })} onUnsure={(k, note) => patch({ unsure: { [k]: note } })} />)}
          <CheckPanel check={draft.check} onAck={(id) => patch({ acknowledged: { [id]: true } })} />
        </TabsContent>
        <TabsContent value="review" className="space-y-6">
          <CheckPanel check={draft.check} onAck={(id) => patch({ acknowledged: { [id]: true } })} />
          <Review draft={draft} onConfirm={confirm} onExport={exportDraft} exporting={exporting} result={result} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
